import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import GroupIcon from "@mui/icons-material/Group";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import MailIcon from "@mui/icons-material/Mail";
import { useNavigate } from "react-router-dom";
import { withApplicationInsights } from "../../../components/telemetry";
import { useTranslation } from "react-i18next";
import { CoachingPageHeader } from "../../../components/coachingNavigator/coachingPageHeader";

type NavItem = {
  to: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  external?: boolean;
};

const CoachingManagement: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const navItems: NavItem[] = useMemo(
    () => [
      {
        to: "/management/methods",
        title: t("coachingNavigator.management.methodsTitle"),
        description: t("coachingNavigator.management.methodsDescription"),
        icon: <MenuBookOutlinedIcon sx={{ fontSize: 32 }} />,
      },
      {
        to: "/management/clients",
        title: t("coachingNavigator.management.clientsTitle"),
        description: t("coachingNavigator.management.clientsDescription"),
        icon: <GroupOutlinedIcon sx={{ fontSize: 32 }} />,
      },
      {
        to: "/management/email-templates",
        title: t("coachingNavigator.management.emailTemplatesTitle"),
        description: t(
          "coachingNavigator.management.emailTemplatesDescription",
        ),
        icon: <MailOutlineOutlinedIcon sx={{ fontSize: 32 }} />,
      },
    ],
    [t],
  );

  return (
    <Box
      sx={{ pt: 2, height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CoachingPageHeader
        title={t("coachingNavigator.management.managementHeader")}
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 2,
          pt: 2,
        }}
      >
        {navItems.map((item) => (
          <Card
            key={item.to}
            variant="outlined"
            sx={{
              borderColor: "divider",
              backgroundColor: "white",
              borderRadius: 3,
              boxShadow: 0,
              transition: "all 0.25s ease-out",
              "&:hover": {
                transform: "translateY(-3px)",
                boxShadow: 4,
                borderColor: alpha(theme.palette.primary.main, 0.4),
                ".nav-icon-box": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.14),
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  color: theme.palette.primary.main,
                },
                ".nav-arrow": { opacity: 1, transform: "translateX(3px)" },
              },
            }}
          >
            <CardActionArea
              onClick={() =>
                item.external
                  ? window.open(item.to, "_blank", "noopener,noreferrer")
                  : navigate(item.to)
              }
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                p: 3,
              }}
            >
              <CardContent
                sx={{
                  p: 0,
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 2,
                  "&:last-child": { pb: 0 },
                }}
              >
                {/* Icon */}
                <Box
                  className="nav-icon-box"
                  sx={{
                    width: 72,
                    height: 72,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 3,
                    backgroundColor: alpha(theme.palette.primary.main, 0.07),
                    color: theme.palette.primary.main,
                    border: "1px solid",
                    borderColor: alpha(theme.palette.primary.main, 0.14),
                    position: "relative",
                    transition: "all 0.25s ease",
                    mb: 1,
                  }}
                >
                  <Box
                    className="nav-icon-outline"
                    sx={{
                      display: "flex",
                      opacity: 1,
                      transition: "opacity 0.2s ease",
                    }}
                  >
                    {item.icon}
                  </Box>
                </Box>

                {/* Title + description */}
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{
                    fontWeight: 700
                  }}>
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      lineHeight: 1.6
                    }}>
                    {item.description}
                  </Typography>
                </Box>

                {/* Arrow */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "primary.main",
                    mt: 1,
                  }}
                >
                  <Typography
                    className="nav-arrow"
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: "primary.main",
                      transition: "all 0.2s ease",
                      transform: "translateX(0)"
                    }}>
                    {t("coachingNavigator.management.open") || "Open"}
                  </Typography>
                  <ArrowForwardIosRoundedIcon
                    className="nav-arrow"
                    sx={{
                      fontSize: "0.75rem",
                      transition: "all 0.2s ease",
                      transform: "translateX(0)",
                    }}
                  />
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default withApplicationInsights(
  CoachingManagement,
  "CoachingManagement",
);
